import type { Product, ProductPrice } from '@titularis/api-client';

/**
 * O nome comercial de um plano, derivado da duração.
 *
 * **Mora aqui porque três superfícies fazem a mesma derivação** - o servidor,
 * na trilha de auditoria; o admin, na tabela de preços; e o app, no seletor da
 * oferta - e duas delas já tinham divergido no caso geral ("De 3 meses" contra
 * "3 meses") antes de esta função existir. O rótulo nunca é gravado (G-CANONICAL):
 * coluna de rótulo poderia discordar da duração real, e a pergunta que se faz
 * meses depois é quanto acesso a pessoa comprou, não como a tela o chamava.
 *
 * A cópia do servidor continua existindo, em `billing/produtos.ts`, porque ela
 * escreve português dentro de frase ("mudou o preço **mensal** de cartão") e
 * este pacote não chega lá. As duas precisam concordar, e é por isso que o caso
 * geral é o mesmo dos dois lados.
 */
export function rotuloDoPlano(accessMonths: number): string {
  if (accessMonths === 1) return 'Mensal';
  if (accessMonths === 6) return 'Semestral';
  if (accessMonths === 12) return 'Anual';
  return `${accessMonths} meses`;
}

export interface PlanoDoProduto {
  accessMonths: number;
  recurring: boolean;
  /** Do mais barato ao mais caro. O Pix vem primeiro, e é onde queremos o olho. */
  precos: ProductPrice[];
}

/**
 * Os planos de um produto, do compromisso mais curto ao mais longo.
 *
 * **Duração é a identidade do plano, e não a forma de pagamento.** Agrupar por
 * `kind` repetiria o desenho de quando havia um preço por forma, em que Pix e
 * cartão pareciam duas ofertas; agrupar por `accessMonths` mantém as formas
 * como alternativas da mesma decisão.
 *
 * **Mora aqui, e não no app, porque a landing quebrou por não ter isto.** Ela
 * tinha a própria leitura do catálogo, chaveada por forma de pagamento, e no
 * dia em que o produto passou a ter cinco preços ela mostrou cinco chips - três
 * deles truncados em "C…" - e anunciou o mensal de R$ 89 como "à vista no Pix",
 * com uma economia calculada contra o anual. Nada falhou: as duas telas apenas
 * discordavam sobre o que o mesmo catálogo significa (G-ISONOMIA).
 */
export function planosDe(produto: Pick<Product, 'precos'>): PlanoDoProduto[] {
  const porDuracao = new Map<number, ProductPrice[]>();

  for (const preco of produto.precos) {
    const precos = porDuracao.get(preco.accessMonths) ?? [];
    precos.push(preco);
    porDuracao.set(preco.accessMonths, precos);
  }

  return [...porDuracao.entries()]
    .sort(([mesesA], [mesesB]) => mesesA - mesesB)
    .map(([accessMonths, precos]) => ({
      accessMonths,
      recurring: precos.some((preco) => preco.recurring),
      precos: [...precos].sort((a, b) => a.amountCents - b.amountCents),
    }));
}
